/**
 * Composant de carte d'aventure - Identique à Flutter BaseHomeCardWidget
 */

import React, {useState, useRef, useEffect} from 'react';
import {
  View,
  Text,
  Image,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  Platform,
  Modal,
  ScrollView,
  Animated,
  Easing,
} from 'react-native';
import {FontAwesomeIcon} from '@fortawesome/react-native-fontawesome';
import {
  faBook,
  faCheck,
  faSync,
  faDownload,
  faPlay,
  faInfoCircle,
  faPalette,
  faBolt,
  faFolder,
  faTimes,
  faArrowLeft,
} from '@fortawesome/free-solid-svg-icons';
import {ListAdventure} from '../models/Adventure';
import {LightTheme} from '../theme/theme';
import {Fonts} from '../config/fonts';

interface AdventureCardProps {
  adventure: ListAdventure;
  onPlay: () => void;
  onDownloadOrUpdate: (adventure: ListAdventure) => Promise<void>;
  isDownloading?: boolean;
  downloadProgress?: number;
}

const AdventureCard: React.FC<AdventureCardProps> = ({
  adventure,
  onPlay,
  onDownloadOrUpdate,
  isDownloading = false,
  downloadProgress = 0,
}) => {
  const [showDetails, setShowDetails] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [imageError, setImageError] = useState(false);
  const modalSlideAnim = useRef(new Animated.Value(100)).current;
  const modalOpacityAnim = useRef(new Animated.Value(0)).current;
  const modalContentScaleAnim = useRef(new Animated.Value(0.95)).current;
  const modalBackdropAnim = useRef(new Animated.Value(0)).current;

  // Fonction pour fermer avec animation
  const handleClose = () => {
    if (isClosing) return; // Déjà en train de fermer
    setIsClosing(true);
    
    // Lancer les animations de fermeture
    Animated.parallel([
      Animated.timing(modalBackdropAnim, {
        toValue: 0,
        duration: 250,
        easing: Easing.in(Easing.cubic),
        useNativeDriver: true,
      }),
      Animated.timing(modalOpacityAnim, {
        toValue: 0,
        duration: 300,
        easing: Easing.in(Easing.cubic),
        useNativeDriver: true,
      }),
      Animated.timing(modalSlideAnim, {
        toValue: 100,
        duration: 300,
        easing: Easing.in(Easing.bezier(0.55, 0.06, 0.68, 0.19)),
        useNativeDriver: true,
      }),
      Animated.timing(modalContentScaleAnim, {
        toValue: 0.95,
        duration: 300,
        easing: Easing.in(Easing.bezier(0.55, 0.06, 0.68, 0.19)),
        useNativeDriver: true,
      }),
    ]).start(() => {
      // Attendre la fin de l'animation avant de fermer réellement
      setShowDetails(false);
      setIsClosing(false);
    });
  };

  // Animation d'ouverture de la modale - Transitions fluides et visibles
  useEffect(() => {
    if (showDetails && !isClosing) {
      // Réinitialiser les valeurs d'animation au début
      modalSlideAnim.setValue(100);
      modalOpacityAnim.setValue(0);
      modalContentScaleAnim.setValue(0.95);
      modalBackdropAnim.setValue(0);
      
      // Ouverture : fade + slide-up + scale pour un effet élégant
      Animated.parallel([
        // Backdrop fade-in
        Animated.timing(modalBackdropAnim, {
          toValue: 1,
          duration: 300,
          easing: Easing.out(Easing.cubic),
          useNativeDriver: true,
        }),
        // Content opacity
        Animated.timing(modalOpacityAnim, {
          toValue: 1,
          duration: 400,
          easing: Easing.out(Easing.cubic),
          useNativeDriver: true,
        }),
        // Content slide-up
        Animated.timing(modalSlideAnim, {
          toValue: 0,
          duration: 450,
          easing: Easing.out(Easing.bezier(0.25, 0.46, 0.45, 0.94)),
          useNativeDriver: true,
        }),
        // Content scale (subtle)
        Animated.timing(modalContentScaleAnim, {
          toValue: 1,
          duration: 450,
          easing: Easing.out(Easing.bezier(0.25, 0.46, 0.45, 0.94)),
          useNativeDriver: true,
        }),
      ]).start();
    }
  }, [showDetails, isClosing]);

  const isDownloaded = adventure.localVersion != null;
  const needsUpdate = isDownloaded && adventure.localVersion! < adventure.version;

  // Construire l'URI de l'image (base64 ou URL)
  const imageUri = adventure.image 
    ? (adventure.image.startsWith('data:image') ? adventure.image : `data:image/png;base64,${adventure.image}`)
    : adventure.imageUrl || null;

  const renderImage = () => {
    if (imageError || !imageUri) {
      return (
        <View style={styles.placeholderImage}>
          <FontAwesomeIcon icon={faBook} size={48} color="#999" />
          <Text style={styles.placeholderText}>Aventure</Text>
        </View>
      );
    }

    if (imageUri.startsWith('data:')) {
      // Image base64
      return (
        <Image
          source={{uri: imageUri}}
          style={styles.image}
          resizeMode="cover"
          onError={() => setImageError(true)}
        />
      );
    }

    // Image réseau
    return (
      <Image
        source={{uri: imageUri}}
        style={styles.image}
        resizeMode="cover"
        onError={() => setImageError(true)}
      />
    );
  };

  return (
    <>
      <TouchableOpacity
        style={styles.card}
        onPress={() => {
          // Action principale : lancer l'aventure ou télécharger
          if (isDownloaded && !isDownloading) {
            onPlay();
          } else if (!isDownloading) {
            onDownloadOrUpdate(adventure);
          }
        }}
        activeOpacity={0.95}>
        <View style={styles.imageContainer}>
          {renderImage()}
          {/* Overlay gradient */}
          <View style={[StyleSheet.absoluteFillObject, styles.gradientOverlayContent]} />
          
          {/* Icône détail - discrète en haut à droite */}
          <TouchableOpacity
            style={styles.detailIconButton}
            onPress={(e) => {
              e.stopPropagation(); // Empêcher le tap sur la carte
              setShowDetails(true);
            }}
            activeOpacity={0.7}>
            <FontAwesomeIcon icon={faInfoCircle} size={16} color="rgba(255, 255, 255, 0.6)" />
          </TouchableOpacity>
          
          {/* Badges - "Téléchargé" discret (icône seule) */}
          {isDownloaded && !needsUpdate && (
            <View style={[styles.badge, styles.downloadedBadge]}>
              <FontAwesomeIcon icon={faCheck} size={14} color="#fff" />
            </View>
          )}
          {needsUpdate && (
            <View style={[styles.badge, styles.updateBadge]}>
              <FontAwesomeIcon icon={faSync} size={16} color="#fff" style={{marginRight: 4}} />
              <Text style={styles.badgeText}>Mise à jour</Text>
            </View>
          )}

          {/* Titre et tags */}
          <View style={styles.imageFooter}>
            <Text style={styles.adventureTitle} numberOfLines={2}>
              {adventure.name}
            </Text>
            <View style={styles.tagsContainer}>
              {adventure.theme && (
                <View style={[styles.tag, styles.themeTag]}>
                  <Text style={styles.tagText}>{adventure.theme}</Text>
                </View>
              )}
              {adventure.difficulty && (
                <View style={[styles.tag, styles.difficultyTag]}>
                  <Text style={styles.tagText}>{adventure.difficulty}</Text>
                </View>
              )}
            </View>
          </View>
        </View>

        {/* Bande narrative (pas un bouton) */}
        <View style={styles.actionsContainer}>
          {isDownloading ? (
            <View style={styles.downloadProgressContainer}>
              <View style={styles.progressBar}>
                <View
                  style={[
                    styles.progressFill,
                    {width: `${Math.min(downloadProgress * 100, 100)}%`},
                  ]}
                />
              </View>
              <Text style={styles.progressText}>
                {Math.round(downloadProgress * 100)}%
              </Text>
            </View>
          ) : !isDownloaded ? (
            <TouchableOpacity
              style={styles.narrativeBand}
              onPress={(e) => {
                e.stopPropagation();
                onDownloadOrUpdate(adventure);
              }}
              activeOpacity={0.9}>
              <Text style={styles.narrativeBandText}>⬇ TÉLÉCHARGER</Text>
            </TouchableOpacity>
          ) : (
            <>
              {/* Bande narrative intégrée - pas un bouton */}
              <View style={styles.narrativeBand}>
                <Text style={styles.narrativeBandText}>▶︎ Commencer l'aventure</Text>
              </View>
              {needsUpdate && (
                <TouchableOpacity
                  style={styles.updateBand}
                  onPress={(e) => {
                    e.stopPropagation();
                    onDownloadOrUpdate(adventure);
                  }}
                  activeOpacity={0.9}>
                  <View style={styles.updateBandContent}>
                    <FontAwesomeIcon icon={faSync} size={14} color="#6B5B4A" />
                    <Text style={styles.updateBandText}> Mise à jour disponible</Text>
                  </View>
                </TouchableOpacity>
              )}
            </>
          )}
        </View>
      </TouchableOpacity>

      {/* Modal de détails - Style 4e de couverture narrative */}
      <Modal
        visible={showDetails}
        transparent
        animationType="none"
        onRequestClose={handleClose}>
        <Animated.View
          style={[
            styles.modalOverlay,
            {
              opacity: modalBackdropAnim,
            },
          ]}>
          <TouchableOpacity
            style={styles.modalBackdrop}
            activeOpacity={1}
            onPress={handleClose}>
            <Animated.View
              style={[
                styles.modalContent,
                {
                  opacity: modalOpacityAnim,
                  transform: [
                    {translateY: modalSlideAnim},
                    {scale: modalContentScaleAnim},
                  ],
                },
              ]}>
              <TouchableOpacity
                style={styles.modalInnerContent}
                activeOpacity={1}
                onPress={(e) => e.stopPropagation()}>
                {/* Texture papier */}
                <View style={styles.modalPaperTexture} />
                
                <ScrollView 
                  style={styles.modalScroll} 
                  showsVerticalScrollIndicator={false}
                  contentContainerStyle={styles.modalScrollContent}>
                  
                  {/* Illustration en haut - partiellement visible, floutée/estompée */}
                  <View style={styles.modalImageContainer}>
                    <View style={styles.modalImageWrapper}>
                      {renderImage()}
                    </View>
                    {/* Overlay flouté/estompé pour ambiance */}
                    <View style={styles.modalImageOverlay} />
                    {/* Gradient doux vers le fond papier */}
                    <View style={styles.modalImageGradient} />
                  </View>

                  {/* Bouton retour discret en haut */}
                  <TouchableOpacity
                    style={styles.modalCloseButton}
                    onPress={handleClose}
                    activeOpacity={0.7}>
                    <FontAwesomeIcon icon={faArrowLeft} size={18} color="rgba(139, 115, 85, 0.7)" />
                  </TouchableOpacity>

                  {/* Contenu principal */}
                  <View style={styles.modalBody}>
                    {/* Titre - Typographie serif (Playfair Display) - Ancre visuelle principale */}
                    <Text style={styles.modalTitle}>{adventure.name}</Text>

                    {/* Métadonnées discrètes - Badges SF / D6 discrets */}
                    <View style={styles.metadataContainer}>
                      {adventure.theme && (
                        <View style={styles.metadataChip}>
                          <Text style={styles.metadataChipText}>{adventure.theme}</Text>
                        </View>
                      )}
                      {adventure.difficulty && (
                        <View style={styles.metadataChip}>
                          <Text style={styles.metadataChipText}>{adventure.difficulty}</Text>
                        </View>
                      )}
                    </View>

                    {/* Description - Texte lisible, aéré, sensation de lecture */}
                    {adventure.description && (
                      <Text style={styles.descriptionText}>
                        {adventure.description}
                      </Text>
                    )}

                    {/* Version locale en note secondaire - discrète */}
                    {isDownloaded && (
                      <Text style={styles.versionNote}>
                        Version locale {adventure.localVersion}
                      </Text>
                    )}
                    {needsUpdate && (
                      <Text style={styles.versionNote}>
                        Mise à jour disponible (v{adventure.version})
                      </Text>
                    )}
                  </View>
                </ScrollView>

                {/* Action finale - Bande narrative éditoriale (pas un bouton classique) */}
                <View style={styles.modalActions}>
                  {isDownloading ? (
                    <View style={styles.downloadProgressContainer}>
                      <View style={styles.progressBar}>
                        <View
                          style={[
                            styles.progressFill,
                            {width: `${Math.min(downloadProgress * 100, 100)}%`},
                          ]}
                        />
                      </View>
                      <Text style={styles.progressText}>
                        {Math.round(downloadProgress * 100)}%
                      </Text>
                    </View>
                  ) : !isDownloaded ? (
                    <TouchableOpacity
                      style={styles.modalNarrativeBand}
                      onPress={() => {
                        handleClose();
                        // Attendre un peu avant de déclencher l'action pour que l'animation se joue
                        setTimeout(() => onDownloadOrUpdate(adventure), 350);
                      }}
                      activeOpacity={0.95}>
                      <Text style={styles.modalNarrativeBandText}>Télécharger l'aventure</Text>
                    </TouchableOpacity>
                  ) : (
                    <>
                      <TouchableOpacity
                        style={styles.modalNarrativeBand}
                        onPress={() => {
                          handleClose();
                          // Attendre un peu avant de déclencher l'action pour que l'animation se joue
                          setTimeout(() => onPlay(), 350);
                        }}
                        activeOpacity={0.95}>
                        <Text style={styles.modalNarrativeBandText}>Commencer l'aventure</Text>
                      </TouchableOpacity>
                      {needsUpdate && (
                        <TouchableOpacity
                          style={styles.modalUpdateBand}
                          onPress={() => {
                            handleClose();
                            // Attendre un peu avant de déclencher l'action pour que l'animation se joue
                            setTimeout(() => onDownloadOrUpdate(adventure), 350);
                          }}
                          activeOpacity={0.9}>
                          <View style={styles.updateBandContent}>
                            <FontAwesomeIcon icon={faSync} size={14} color="#6B5B4A" />
                            <Text style={styles.updateBandText}> Mise à jour disponible</Text>
                          </View>
                        </TouchableOpacity>
                      )}
                    </>
                  )}
                </View>
              </TouchableOpacity>
            </Animated.View>
          </TouchableOpacity>
        </Animated.View>
      </Modal>
    </>
  );
};

const styles = StyleSheet.create({
  card: {
    // Style couverture de livre : coins légèrement irréguliers, bordure fine, ombre diffuse
    ...Platform.select({
      web: {
        borderTopLeftRadius: 14,
        borderTopRightRadius: 16,
        borderBottomRightRadius: 16,
        borderBottomLeftRadius: 12,
      },
      default: {
        borderRadius: 14,
      },
    }),
    overflow: 'hidden',
    backgroundColor: '#fff',
    marginBottom: 28, // Plus d'espace entre les cartes
    borderWidth: 1,
    borderColor: 'rgba(0, 0, 0, 0.08)', // Bordure fine
    ...Platform.select({
      web: {
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08), 0 2px 4px rgba(0, 0, 0, 0.06)', // Ombre diffuse
      },
      default: {
        shadowColor: '#000',
        shadowOffset: {width: 0, height: 2},
        shadowOpacity: 0.08, // Très doux
        shadowRadius: 8,
        elevation: 2,
      },
    }),
  },
  imageContainer: {
    height: 300,
    position: 'relative',
  },
  image: {
    width: '100%',
    height: '100%',
  },
  placeholderImage: {
    width: '100%',
    height: '100%',
    backgroundColor: '#e0e0e0',
    justifyContent: 'center',
    alignItems: 'center',
  },
  placeholderIcon: {
    fontSize: 48,
    marginBottom: 8,
  },
  placeholderText: {
    fontSize: 14,
    color: '#666',
    fontWeight: '500',
  },
    gradientOverlayContent: {
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      // Sur web, utiliser backgroundImage au lieu de background
      ...(Platform.OS === 'web' ? {
        backgroundImage: 'linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.5) 100%)',
      } : {}),
    },
  // Icône discrète pour ouvrir le détail
  detailIconButton: {
    position: 'absolute',
    top: 12,
    right: 12,
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: 'rgba(0, 0, 0, 0.2)', // Fond très discret
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10, // Au-dessus des badges
    ...Platform.select({
      web: {
        backdropFilter: 'blur(4px)',
      },
    }),
  },
  badge: {
    position: 'absolute',
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 20,
    ...(Platform.OS === 'web'
      ? {
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
        }
      : {
          shadowColor: '#000',
          shadowOffset: {width: 0, height: 2},
          shadowOpacity: 0.2,
          shadowRadius: 4,
          elevation: 2,
        }),
  },
  downloadedBadge: {
    top: 12,
    right: 50, // Décalé à gauche pour éviter l'icône de détail
    backgroundColor: 'rgba(76, 175, 80, 0.5)', // Très discret, peu contrasté
    width: 26, // Carré petit
    height: 26,
    borderRadius: 13,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 0,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.3)', // Bordure discrète
  },
  updateBadge: {
    top: 12,
    left: 12,
    backgroundColor: 'rgba(255, 152, 0, 0.9)',
  },
  badgeText: {
    color: '#fff',
    fontSize: 11,
    fontWeight: 'bold',
  },
  imageFooter: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    padding: 16,
  },
  adventureTitle: {
    fontFamily: Fonts.title,
    fontSize: 20,
    fontWeight: 'bold',
    color: '#fff',
    marginBottom: 8,
    ...(Platform.OS !== 'web'
      ? {
          textShadowColor: 'rgba(0, 0, 0, 0.75)',
          textShadowOffset: {width: 1, height: 1},
          textShadowRadius: 3,
        }
      : {
          textShadow: '1px 1px 3px rgba(0, 0, 0, 0.75)',
        }),
  },
  tagsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  tag: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    ...Platform.select({
      web: {
        borderTopLeftRadius: 6,
        borderTopRightRadius: 8,
        borderBottomRightRadius: 8,
        borderBottomLeftRadius: 4,
      },
      default: {
        borderRadius: 8,
      },
    }),
  },
  themeTag: {
    backgroundColor: 'rgba(33, 150, 243, 0.75)', // Un peu plus transparent
  },
  difficultyTag: {
    backgroundColor: 'rgba(156, 39, 176, 0.75)', // Un peu plus transparent
  },
  tagText: {
    color: '#fff',
    fontSize: 10,
    fontWeight: '500', // Moins gras
    fontFamily: Fonts.ui,
  },
  actionsContainer: {
    padding: 0,
    backgroundColor: 'transparent',
    borderTopWidth: 0,
  },
  // Bande narrative intégrée - PAS un bouton
  narrativeBand: {
    // Fond beige foncé / brun clair / vert très désaturé (olive)
    backgroundColor: '#8B7355', // Brun beige désaturé, comme du papier vieilli
    paddingVertical: 14,
    paddingHorizontal: 20,
    // Coins suivent la carte (coins inférieurs uniquement)
    ...Platform.select({
      web: {
        borderTopLeftRadius: 0,
        borderTopRightRadius: 0,
        borderBottomRightRadius: 14,
        borderBottomLeftRadius: 12,
      },
      default: {
        borderTopLeftRadius: 0,
        borderTopRightRadius: 0,
        borderBottomRightRadius: 14,
        borderBottomLeftRadius: 12,
      },
    }),
    // Pas d'ombre (hérite de la carte)
    // Pas de margin
    marginTop: 0,
    marginBottom: 0,
    alignItems: 'center',
    justifyContent: 'center',
  },
  narrativeBandText: {
    // Texte serif, blanc cassé ou brun très foncé
    color: '#F5F3ED', // Blanc cassé / beige clair
    fontSize: 15,
    fontWeight: '400', // Pas trop gras
    fontFamily: Fonts.reading, // Serif (Merriweather)
    letterSpacing: 0.5,
    fontStyle: 'italic', // Légèrement italique pour l'aspect narratif
  },
  updateBand: {
    backgroundColor: '#E8D5C4', // Beige clair, discret
    paddingVertical: 10,
    paddingHorizontal: 16,
    marginTop: 0,
    ...Platform.select({
      web: {
        borderTopLeftRadius: 0,
        borderTopRightRadius: 0,
        borderBottomRightRadius: 12,
        borderBottomLeftRadius: 10,
      },
      default: {
        borderTopLeftRadius: 0,
        borderTopRightRadius: 0,
        borderBottomRightRadius: 12,
        borderBottomLeftRadius: 10,
      },
    }),
  },
  updateBandContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  },
  updateBandText: {
    fontSize: 13,
    color: '#6B5B4A', // Brun foncé
    fontFamily: Fonts.ui,
    fontStyle: 'italic',
  },
  actionButtonContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  actionButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: 'bold',
    fontFamily: Fonts.title,
  },
  downloadProgressContainer: {
    padding: 16,
  },
  progressBar: {
    height: 8,
    backgroundColor: '#e0e0e0',
    borderRadius: 4,
    overflow: 'hidden',
    marginBottom: 8,
  },
  progressFill: {
    height: '100%',
    backgroundColor: '#1976d2',
  },
  progressText: {
    textAlign: 'center',
    fontSize: 14,
    fontWeight: 'bold',
    fontFamily: Fonts.title,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.4)', // Fond sombre avec transition fade
  },
  modalBackdrop: {
    flex: 1,
    width: '100%',
    height: '100%',
  },
  modalContent: {
    flex: 1,
    backgroundColor: '#F5F3ED', // Fond papier clair
    width: '100%',
    overflow: 'hidden',
    position: 'relative',
    // Transitions gérées par les animations Animated
  },
  modalPaperTexture: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    opacity: 0.025, // Texture papier très légère (2-3%)
    backgroundColor: '#000',
    ...Platform.select({
      web: {
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
        backgroundRepeat: 'repeat',
      },
    }),
    zIndex: 0,
  },
  modalInnerContent: {
    flex: 1,
    position: 'relative',
    zIndex: 1,
  },
  modalScroll: {
    flex: 1,
  },
  modalScrollContent: {
    paddingBottom: 24,
  },
  modalImageContainer: {
    height: 300, // Partiellement visible
    position: 'relative',
    overflow: 'hidden',
    marginBottom: -40, // Légèrement chevauchant le contenu pour transition douce
  },
  modalImageWrapper: {
    ...StyleSheet.absoluteFillObject,
    opacity: 0.7, // Image légèrement estompée
  },
  modalImageOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(245, 243, 237, 0.6)', // Overlay estompé plus prononcé
    zIndex: 1,
    ...Platform.select({
      web: {
        backdropFilter: 'blur(2px)', // Flou léger sur web
      },
    }),
  },
  modalImageGradient: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: 150,
    ...Platform.select({
      web: {
        backgroundImage: 'linear-gradient(to bottom, transparent 0%, rgba(245, 243, 237, 0.7) 50%, rgba(245, 243, 237, 1) 100%)',
      },
      default: {
        backgroundColor: 'rgba(245, 243, 237, 0.95)',
      },
    }),
    zIndex: 2,
  },
  modalCloseButton: {
    position: 'absolute',
    top: 16,
    left: 16,
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: 'rgba(245, 243, 237, 0.9)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10,
    ...Platform.select({
      web: {
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: {width: 0, height: 1},
        shadowOpacity: 0.08,
        shadowRadius: 2,
        elevation: 2,
      },
    }),
  },
  modalTitle: {
    fontFamily: Fonts.title, // Playfair Display
    fontSize: 36,
    fontWeight: '600',
    color: '#2C2C2C',
    letterSpacing: 0.5,
    marginBottom: 16,
    lineHeight: 44, // Interligne confortable
  },
  modalBody: {
    padding: 28,
    paddingTop: 24,
    backgroundColor: 'transparent',
  },
  metadataContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 28,
  },
  metadataChip: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    ...Platform.select({
      web: {
        borderTopLeftRadius: 6,
        borderTopRightRadius: 4,
        borderBottomRightRadius: 8,
        borderBottomLeftRadius: 4,
      },
      default: {
        borderRadius: 6,
      },
    }),
    borderWidth: 1,
    borderColor: 'rgba(212, 196, 176, 0.5)',
    backgroundColor: 'rgba(232, 213, 196, 0.3)', // Très discret
  },
  metadataChipText: {
    fontSize: 12,
    color: '#8B7355', // Couleur désaturée, discrète
    fontFamily: Fonts.ui,
    opacity: 0.7,
  },
  descriptionText: {
    fontSize: 17,
    lineHeight: 30, // Interligne confortable pour la lecture
    color: '#2C2C2C',
    marginBottom: 24,
    fontFamily: Fonts.reading, // Merriweather pour la lecture
    letterSpacing: 0.2,
  },
  versionNote: {
    fontSize: 13,
    color: '#8B7355',
    fontFamily: Fonts.ui,
    opacity: 0.6,
    fontStyle: 'italic',
    marginTop: 8,
  },
  modalActions: {
    padding: 24,
    paddingTop: 20,
    paddingBottom: 32,
    backgroundColor: 'transparent',
    // Pas de bordure - style narratif
  },
  // Bande narrative éditoriale (pas un bouton classique)
  modalNarrativeBand: {
    backgroundColor: '#6B5B4A', // Brun / beige foncé (jamais vert "success")
    paddingVertical: 18,
    paddingHorizontal: 28,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 12,
    // Pas de border radius - style bande éditoriale
    // Pas d'ombre
  },
  modalNarrativeBandText: {
    color: '#F5F3ED', // Blanc cassé
    fontSize: 16,
    fontWeight: '400',
    fontFamily: Fonts.reading, // Serif (Merriweather)
    letterSpacing: 0.8,
    // Pas d'italic pour l'action principale
  },
  modalUpdateBand: {
    backgroundColor: '#E8D5C4', // Beige clair, discret
    paddingVertical: 10,
    paddingHorizontal: 16,
    ...Platform.select({
      web: {
        borderTopLeftRadius: 10,
        borderTopRightRadius: 8,
        borderBottomRightRadius: 12,
        borderBottomLeftRadius: 6,
      },
      default: {
        borderRadius: 10,
      },
    }),
    marginBottom: 12,
  },
});

export default AdventureCard;
